import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Checkbox } from "@/components/ui/checkbox"
import { Workflow, ArrowLeft, User, Mail, Loader2 } from "lucide-react"
import { Link } from "react-router-dom"
import { navigateToDashboard, setAuthState } from "shared-web"
import { API_BASE_URL, ENDPOINTS, IS_DEV_MODE } from "shared-web"

export default function LoginPage() {
  const [showSSO, setShowSSO] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userProfile, setUserProfile] = useState({
    name: "John Doe",
    email: "john.doe@example.com",
    company: "",
    role: "",
  })

  const handleAuth = async (provider: 'google' | 'internal', email?: string) => {
    setIsLoading(true)
    try {
      if (provider === 'google') {
        // Redirect to Google OAuth only in non-test environment
        window.location.href = `${API_BASE_URL}${ENDPOINTS.AUTH.GOOGLE}`
      } else {
        // Use internal auth for testing or when provider is 'internal'
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.INTERNAL_CALLBACK}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            name: userProfile.name,
          })
        })

        if (!response.ok) {
          throw new Error('Authentication failed')
        }

        const result = await response.json()

        if (result.accessToken && result.user) {
          // Store auth state for cross-app communication
          const authState = {
            token: result.accessToken,
            user: {
              id: result.user.id,
              name: result.user.name,
              email: result.user.email,
              avatar: result.user.avatar
            },
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
          }
          setAuthState(authState)

          // Navigate to dashboard with auth state
          navigateToDashboard()
        } else {
          throw new Error('Invalid authentication response')
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      // Could show error toast here
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = () => {
    // Use internal auth in dev mode, Google OAuth in production
    handleAuth(IS_DEV_MODE ? 'internal' : 'google')
  }

  const handleSSO = () => {
    setShowSSO(!showSSO)
  }

  const handleProfileUpdate = (field: string, value: string) => {
    setUserProfile((prev) => ({ ...prev, [field]: value }))
  }

  if (showOnboarding) {
    return (
      <div className="min-h-screen flex">
        {/* Left Pane - Image/Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-700 via-green-600 to-lime-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
            <div className="mb-8">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                <Workflow className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading font-black text-4xl mb-4">You're almost ready!</h1>
              <p className="font-body text-xl text-green-100 max-w-md leading-relaxed">
                Just a few quick details and you'll be building powerful AI workflows in no time.
              </p>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-32 left-16 w-24 h-24 bg-lime-400/20 rounded-full blur-lg"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-md"></div>
          </div>

          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full"></div>
            <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full"></div>
            <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-white rounded-full"></div>
            <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-white rounded-full"></div>
          </div>
        </div>

        {/* Right Pane - Onboarding Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            <Card className="border-gray-200 shadow-lg">
              <CardHeader className="text-center">
                <div className="flex items-center justify-center mb-4 lg:hidden">
                  <div className="w-10 h-10 bg-green-700 rounded-lg flex items-center justify-center mr-3">
                    <Workflow className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-heading font-black text-2xl text-gray-900">Lavender Flow</span>
                </div>
                <CardTitle className="font-heading font-bold text-2xl text-gray-900 mb-2">
                  Hey, {userProfile.name.split(" ")[0]}! Welcome to Lavender Flow
                </CardTitle>
                <p className="font-body text-gray-600">
                  Let's set up your profile to get you started.
                </p>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-4 mb-5">
                  <div>
                    <Label htmlFor="name" className="font-body text-sm font-medium text-gray-700">
                      Display Name
                    </Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        value={userProfile.name}
                        onChange={(e) => handleProfileUpdate("name", e.target.value)}
                        className="pl-10 font-body h-12 border-purple-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="role" className="font-body text-sm font-medium text-gray-700">
                      Tagline / Pronouns (Optional)
                    </Label>
                    <Input
                      id="role"
                      type="text"
                      value={userProfile.role}
                      onChange={(e) => handleProfileUpdate("role", e.target.value)}
                      className="mt-1 font-body h-12 border-purple-300 focus:border-green-500 focus:ring-green-500"
                      placeholder="e.g., Product Manager, they/her"
                    />
                  </div>
                  {/*<div>
                    <Label htmlFor="company" className="font-body text-sm font-medium text-gray-700">
                      Company (Optional)
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      value={userProfile.company}
                      onChange={(e) => handleProfileUpdate("company", e.target.value)}
                      className="mt-1 font-body h-12 border-gray-300 focus:border-green-500 focus:ring-green-500"
                      placeholder="Enter your company name"
                    />
                  </div>*/}
                  <div>
                    <Label htmlFor="email" className="font-body text-sm font-medium text-gray-700">
                      Email Address
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        disabled={true}
                        value={userProfile.email}
                        onChange={(e) => handleProfileUpdate("email", e.target.value)}
                        className="pl-10 font-body h-12 border-gray-300 bg-gray-50 focus:border-green-500 focus:ring-green-500"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div className="flex items-center text-gray-700 p-2 pt-4 mb-0 pb-0 gap-3">
                      <Checkbox id="ack-updates" />
                      <Label htmlFor="ack-updates">Recieve product updates</Label>
                    </div>
                  </div>
                </div>
                  <p className="text-center font-body text-sm text-gray-600">
                    You can update this information later in your settings.
                  </p>
                <Button className="w-full font-body h-12 bg-green-700 hover:bg-green-800 text-white transition-colors">
                  Accept terms and continue
                </Button>

                <div className="text-center">
                  <p className="font-body text-sm text-gray-700">
                    By selecting Continue, you agree to<br/> our <a href="/legal/terms" className="text-green-700 hover:text-green-800 underline">Terms of Service</a> and <a href="/legal/privacy" className="text-green-700 hover:text-green-800 underline">Privacy Policy</a>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Pane - Image/Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-700 via-green-600 to-lime-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
              <Workflow className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-black text-4xl mb-4">Welcome to Lavender Flow</h1>
            <p className="font-body text-xl text-green-100 max-w-md leading-relaxed">
              Build powerful AI workflows with our visual, node-based platform. Automate everything from email responses
              to complex data pipelines.
            </p>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-32 left-16 w-24 h-24 bg-lime-400/20 rounded-full blur-lg"></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-md"></div>
        </div>

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full"></div>
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-white rounded-full"></div>
          <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-white rounded-full"></div>
        </div>
      </div>

      {/* Right Pane - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Back to home link */}
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-green-700 transition-colors mb-8 font-body"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <Card className="border-gray-200 shadow-lg">
            <CardHeader className="text-center pb-6">
              <div className="flex items-center justify-center mb-4 lg:hidden">
                <div className="w-10 h-10 bg-green-700 rounded-lg flex items-center justify-center mr-3">
                  <Workflow className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading font-black text-2xl text-gray-900">Lavender Flow</span>
              </div>
              <CardTitle className="font-heading font-bold text-2xl text-gray-900 mb-2">
                Sign in to your account
              </CardTitle>
              <p className="font-body text-gray-600">
                Welcome back! Please sign in to continue building your workflows.
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  variant="outline"
                  data-testid="google-login-btn"
                  className="w-full font-body h-12 border-gray-300 hover:bg-gray-50 transition-colors bg-transparent"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  {IS_DEV_MODE ? 'Continue with Dev Login (Google)' : 'Continue with Google'}
                </Button>

                <Button
                  onClick={() => handleAuth('internal')}
                  disabled={isLoading}
                  variant="outline"
                  data-testid="microsoft-login-btn"
                  className="w-full font-body h-12 border-gray-300 hover:bg-gray-50 transition-colors bg-transparent"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#00BCF2" d="M24 12c0 6.624-5.376 12-12 12S0 18.624 0 12 5.376 0 12 0s12 5.376 12 12z" />
                      <path
                        fill="#FFF"
                        d="M12.017 5.4H8.4v3.6h3.617c-.043.729-.4 1.371-.971 1.8-.514.386-1.2.6-1.929.6-1.457 0-2.743-1.029-3.086-2.4-.086-.343-.129-.7-.129-1.071 0-.372.043-.729.129-1.072C6.374 6.429 7.66 5.4 9.117 5.4c.8 0 1.5.286 2.057.8l2.4-2.4C12.46 2.743 10.86 2.057 9.117 2.057 5.66 2.057 2.743 4.8 2.4 8.229c-.171.686-.257 1.4-.257 2.114 0 .714.086 1.429.257 2.114.343 3.429 3.26 6.171 6.717 6.171 1.743 0 3.343-.686 4.457-1.743 1.2-1.114 1.886-2.743 1.886-4.571 0-.343-.029-.686-.086-1.029H12.017V5.4z"
                      />
                    </svg>
                  )}
                  Continue with Microsoft
                </Button>

                <Button
                  onClick={() => handleAuth('internal')}
                  disabled={isLoading}
                  variant="outline"
                  data-testid="github-login-btn"
                  className="w-full font-body h-12 border-gray-300 hover:bg-gray-50 transition-colors bg-transparent"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.097.118.112.221.083.343-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378 0 0-.599 2.282-.744 2.84-.282 1.084-1.064 2.456-1.549 3.235C9.584 23.815 10.77 24.001 12.017 24.001c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" />
                    </svg>
                  )}
                  {IS_DEV_MODE ? 'Continue with Dev Login' : 'Continue with GitHub'}
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500 font-body">Or</span>
                </div>
              </div>

              {!showSSO ? (
                <Button
                  onClick={handleSSO}
                  disabled={isLoading}
                  variant="outline"
                  data-testid="sso-login-btn"
                  className="w-full font-body h-12 border-gray-300 hover:bg-gray-50 transition-colors bg-transparent"
                >
                  Sign in with SSO
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="sso-email" className="font-body text-sm font-medium text-gray-700">
                      Work Email
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="sso-email"
                        type="email"
                        data-testid="sso-email-input"
                        className="pl-10 font-body h-12 border-gray-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="Enter your work email"
                        value={userProfile.email}
                        onChange={(e) => handleProfileUpdate("email", e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAuth('internal', userProfile.email)}
                      disabled={isLoading}
                      data-testid="sso-continue-btn"
                      className="flex-1 font-body h-12 bg-green-700 hover:bg-green-800 text-white transition-colors"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      Continue with SSO
                    </Button>
                    <Button
                      onClick={() => setShowSSO(false)}
                      disabled={isLoading}
                      variant="outline"
                      data-testid="sso-cancel-btn"
                      className="px-4 font-body h-12 border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="font-body text-sm text-gray-600">
                  New to Lavender? <span className="text-green-700 font-medium">Just sign in to get started!</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
